/* 
 * @props isPopup: (boolean) - If passed as true, then component will be shown inside a modal
 * @props isPopupOpen: (boolean) - State of opening/closing of modal
 * @props onPopupCloseCb: (callback function) - Callback Function which will get called when the modal is closed
 * @props recipientID - ID of the recipient. If this prop is passed then component will fetch the details of this id.
 * @props recipientData - We can also pass the whole object with recipient details to save the API call.
 * @props fromNecGrid (boolean) - If we choose to edit recipient from NEC grid page, Set it as true
 * props submitFormCb (function) - This callback function will recieve all the form fields.
*/

import React, { useEffect, useState } from 'react'
import styles from "../AddRecipient.styles"
import { useDispatch, useSelector } from 'react-redux';
import { componentKey, setMethodType, setRecipientAddressInformation, setRecipientAddressInformationFieldsTouched, setRecipientBasicInformation, setRecipientBasicInformationFieldsTouched, setRecipientDetailId, setRecipientEditSuccessfull, setRecipientOptionalInformation, setRecipientOptionalInformationFieldsTouched, setRecipientType, setRequestType, setSelectedPayer, setTinNotProviderState, setZipCodeValidationSuccess } from '../AddRecipientSlice';
import Loadable from '../../../../components/loadable/Loadable';
import StepperNavigation from '../../../../components/steppernavigation/StepperNavigation';
import { addNotifications } from '../../../toaster/ToasterPortalSlice';
import GeneralValidator, { generalValidator } from '../../../../../libs/utility/validators/GeneralValidator';
import useGarbageCollector from '../../../../../libs/garbagecollector/garbageCollectorHooks';
import GetRequestType from "../../addrecipientnew/steps/getrequesttype/GetRequestType"
import GetBasicInfo from "../../addrecipientnew/steps/getbasicinfo/GetBasicInfo";
import GetAddressInfo from "../../addrecipientnew/steps/getaddressinfo/GetAddressInfo"
import GetOptionalInfo from "../../addrecipientnew/steps/getoptionalinfo/GetOptionalInfo"
import { editRecipient, getRecipientDataById, validateZipCode } from '../AddRecipientSaga';
import { RECIPIENT_SECTION_ROUTES } from '../../../recipientssection/constants';
import Popup from '../../../../components/popup/Popup';
import { componentKey as dataLoaderKey } from '../../../loaders/dataloader/DataLoaderSlice';
import AddRecipientSuccess from '../addrecipentsuccess/AddRecipientSuccess';
import W8W9PaymentModals from '../w8w9paymentmodals/W8W9PaymentModals';
import { RECIPIENT_TYPE_1042_S, RECIPIENT_TYPE_TITLE } from '../getrecipienttype/constants';
import GetRecipientType from '../getrecipienttype/GetRecipientType';
import { VALIDATE_NAME_REGEX, VALIDATE_ZIP_CODE_REGEX } from '../../../../../libs/utility/constants';
import useTxNavigate from '../../../../../libs/txnavigate/TxNavigateHook';
import { ADD_RECIPIENT_LOADING_STATES, ADD_RECIPIENT_MESSAGE_MAP } from '../constant';
import AllStepsSingleView from '../../payerandrecipientallstepssingleview/AllStepsSingleView';
import { FEATURE_FLAG } from '../../../../featureFlags';
import Recipient from '../../../../../libs/utility/Recipient';
import { getCountriesAndStatesList } from '../../../loaders/dataloader/DataLoaderSaga';

export default function EditRecipient({ isPopup = false, isPopupOpen = false, onPopupCloseCb = () => { }, recipientID = null, recipientData = {}, fromNecGrid = false, fromFormModule = false, submitFormCb = (formData) => { }, payer = { label: "", value: "" }, onFinalSubmitCb = () => { }, is1042SForm = false, loaderProps = {} }) {

    useGarbageCollector(componentKey);

    const navigate = useTxNavigate();
    const dispatch = useDispatch();

    const editRecipientData = useSelector(state => state[componentKey]);
    const { loadingState, RecipientType, RequestType, recipientBasicInfo, recipientAddressInformation,
        recipientOptionalInformation, zipCodeValidationSuccess, recipientEditedSuccessful, submittedRecipient } = editRecipientData;

    const { countriesList, statesList } = useSelector((state) => state[dataLoaderKey]);

    const [refreshStep, setRefreshStep] = useState(0);
    const [switchToAllStepsSingleView, setSwitchToAllStepsSingleView] = useState(false);
    
    const { tinTooltip, tinPlaceholder, tinValue, tinRules, showNames, tinDisabled } = Recipient.getTINProps(RecipientType, recipientBasicInfo.RecipientTIN?.value, RequestType, recipientBasicInfo?.tinNotProvided);

    // Get recipient details by ID
    useEffect(() => {
        if (recipientID && !recipientData?.RecipentDetailId) {
            dispatch(getRecipientDataById({ RecipentDetailId: recipientID, is1042SForm, statesList, countriesList }))
        } else if (!recipientID && !recipientData?.RecipentDetailId && !fromNecGrid) {
            if (isPopup) {
                onPopupCloseCb();
            } else {
                navigate(`/${RECIPIENT_SECTION_ROUTES.RECIPIENT}`)
            }
        }
    }, []);

    useEffect(() => {
        if (!RequestType) {
            const USA = countriesList?.find(country => country?.Country_code === "US")
            dispatch(setRecipientAddressInformation({ RecipientCountry: USA }));
        }
    }, []);

    useEffect(() => {
        if (countriesList.length <= 0 || statesList.length <= 0) {
            dispatch(getCountriesAndStatesList())
        };
    }, [])


    useEffect(() => {
        if (recipientAddressInformation.RecipientCountry.value !== "US") {
            dispatch(setRecipientAddressInformation({
                RecipientState: {
                    label: "",
                    value: "",
                    errors: {},
                    rules: { required: false }
                },
            }));

            dispatch(setRecipientAddressInformation({
                RecipientZIP: {
                    value: "",
                    errors: {},
                    rules: { required: false, regex: { pattern: VALIDATE_ZIP_CODE_REGEX, message: "Please enter a valid ZIP Code. The ZIP Code should only contain numbers" } }
                }
            }));
        } else if (recipientAddressInformation.RecipientCountry.value === "US") {
            dispatch(setRecipientAddressInformation({
                RecipientState: {
                    rules: { required: true }
                },
            }));

            dispatch(setRecipientAddressInformation({
                RecipientZIP: {
                    value: recipientAddressInformation?.RecipientZIP?.value || recipientData?.ZipCode || "",
                    rules: { required: true, regex: { pattern: VALIDATE_ZIP_CODE_REGEX, message: "Please enter a valid ZIP Code. The ZIP Code should only contain numbers" } }
                }
            }));
        }
    }, [recipientAddressInformation?.RecipientCountry.value, recipientData?.ZipCode])

    // Set recipient state if data is passed as props
    useEffect(() => {

        if (!recipientData?.RecipentDetailId && !fromNecGrid) {
            return;
        }

        if (!fromNecGrid && !payer?.PayerDetailId) {
            dispatch(addNotifications({ heading: "Error", message: "Missing Payer ID" }));
            return;
        }

        if (!fromNecGrid) {
            dispatch(setRecipientDetailId(recipientData?.RecipentDetailId));
        }

        dispatch(setSelectedPayer(payer))
        dispatch(setRequestType(""))

        if (is1042SForm) {
            const recipientType = RECIPIENT_TYPE_1042_S.find(r => r.value === recipientData?.IdType);

            if (recipientType) {
                dispatch(setRecipientType(recipientType?.title || ""));
            }
        } else {
            dispatch(setRecipientType(recipientData.IdType === "SSN" ? RECIPIENT_TYPE_TITLE.INDIVIDUAL : RECIPIENT_TYPE_TITLE.BUSINESS));
        }

        dispatch(setTinNotProviderState(recipientData?.IdNumber ? false : true))

        dispatch(setRecipientBasicInformation({
            RecipientFirstName: { value: recipientData?.FirstName || "" },
            RecipientMiddleName: { value: recipientData?.MiddleName || "" },
            RecipientLastName: { value: recipientData?.LastName || "" },
            RecipientTIN: { value: recipientData?.IdNumber || "" },
            RecipientEmail: { value: recipientData?.emailid || "" },
            RecipientPhNo: { value: recipientData?.Phone || "" },
            RFilerType: { value: "" },
            RecipientSuffix: { value: recipientData?.Suffix || "" },
        }))

        dispatch(setRecipientAddressInformation({
            RecipientAddress: { value: recipientData?.Address1 || "" },
            RecipientAddress2: { value: recipientData?.Address2 || "" },
            RecipientCity: { value: recipientData?.City },
        }))

        dispatch(setRecipientOptionalInformation({
            AttentionTo: { value: recipientData?.AttentionTo || "" },
            Isrecipientclient: { value: recipientData?.ClientRecipientId || "" },
        }))

        dispatch(setRecipientBasicInformationFieldsTouched(true))
        dispatch(setRecipientAddressInformationFieldsTouched(true))
        dispatch(setRecipientOptionalInformationFieldsTouched(true))
        dispatch(setZipCodeValidationSuccess(true))

        const country = countriesList?.find(country => country?.Country_code === recipientData?.Country)

        let state = { label: recipientData?.State, value: recipientData?.State };
        if (recipientData?.Country === "US") {
            state = statesList.find(state => state.StateAbbreviation === recipientData?.State);
        }

        dispatch(setRecipientAddressInformation({ RecipientCountry: country }))
        dispatch(setRecipientAddressInformation({ RecipientState: state }))
        dispatch(setRecipientAddressInformation({ RecipientZIP: { value: recipientData?.ZipCode || "" } }))

        if (recipientData?.Country === "US") {
            dispatch(setZipCodeValidationSuccess(false))
        } else {
            dispatch(setZipCodeValidationSuccess(true))
        }
    }, [recipientData]);

    useEffect(() => {
        if (recipientEditedSuccessful) {
            if (isPopup) {
                setRefreshStep(0);
                onFinalSubmitCb();
                onPopupCloseCb();
            }
        }
    }, [recipientEditedSuccessful])

    useEffect(() => {
        let firstName = '';
        let middleName = '';
        let lastName = '';
        if (recipientID && !recipientData?.RecipentDetailId) {
            firstName = recipientBasicInfo?.RecipientFirstName?.value || "";
            middleName = recipientBasicInfo?.RecipientMiddleName?.value || "";
            lastName = recipientBasicInfo?.RecipientLastName?.value || "";
        }else{
            firstName = recipientData?.FirstName || "";
            middleName = recipientData?.MiddleName || "";
            lastName = recipientData?.LastName || "";
        }
        if (RecipientType === RECIPIENT_TYPE_TITLE.BUSINESS) {
            dispatch(setRecipientBasicInformation({
                RecipientFirstName: {
                    value: firstName,
                    errors: {},
                    rules: { required: false }
                },
            }));

            dispatch(setRecipientBasicInformation({
                RecipientMiddleName: {
                    value: middleName,
                    errors: {},
                    rules: { required: false }
                },
            }))
        } else {
            dispatch(setRecipientBasicInformation({
                RecipientFirstName: {
                    value: firstName,
                    errors: {},
                    rules: { required: false, regex: { pattern: VALIDATE_NAME_REGEX, message: "A valid recipient name should only consist any of A-Z,a-z, 0-9,-,.&' letters and maximum 40 character" } }
                },
            }));

            dispatch(setRecipientBasicInformation({
                RecipientMiddleName: {
                    value: middleName,
                    errors: {},
                    rules: { required: false, regex: { pattern: VALIDATE_NAME_REGEX, message: "A valid recipient name should only consist any of A-Z,a-z, 0-9,-,.&' letters and maximum 40 character" } }
                },
            }))
        }
        dispatch(setRecipientBasicInformation({
            RecipientTIN: {
                rules: {...tinRules}
            }
        }));
    }, [RecipientType])

    useEffect(() => {
        dispatch(setRecipientBasicInformation({ ["RecipientEmail"]: { rules: { required: RequestType ? true : false } } }));
    }, [RequestType]);

    useEffect(() => {
        const isRequiredField = RequestType ? false : true
        const addressFieldsRules = {
            RecipientAddress: {
                rules: {
                    ...recipientAddressInformation.RecipientAddress.rules,
                    required: isRequiredField
                }
            },
            RecipientCountry: {
                rules: {
                    ...recipientAddressInformation.RecipientCountry?.rules,
                    required: isRequiredField
                }
            },
            RecipientState: {
                rules: {
                    ...recipientAddressInformation.RecipientState?.rules,
                    required: isRequiredField
                }
            },
            RecipientCity: {
                rules: {
                    ...recipientAddressInformation.RecipientCity?.rules,
                    required: isRequiredField
                }
            },
            RecipientZIP: {
                rules: {
                    ...recipientAddressInformation.RecipientZIP?.rules,
                    required: isRequiredField
                }
            }
        }
        const basicInfoFieldsRules = {
            RecipientLastName: {
                rules: {
                    ...recipientBasicInfo.RecipientLastName?.rules,
                    required: isRequiredField
                }
            },
            RecipientTIN: {
                rules: {...tinRules}
            }
        }
        dispatch(setRecipientAddressInformation(addressFieldsRules))
        dispatch(setRecipientBasicInformation(basicInfoFieldsRules))
    }, [RequestType])

    const EDIT_RECIPIENT_STEPS = [
        ...(fromFormModule ? [] : [{
            title: "Type of request",
            stepBodyComponent: <GetRequestType onCardClickCb={(card) => dispatch(setRequestType(RequestType == card.title ? '' : card.title))} type={RequestType} />,
            ...(!isPopup
                ? {
                    backButtonText: "Back",
                    prevCb: () => {
                      navigate(`/${RECIPIENT_SECTION_ROUTES.RECIPIENT}`);
                    },
                  }
                : {}),
            submitButtonText: "Next",
            submitCb: () => { },
        }]),
        {
            title: "Type of recipient",
            stepBodyComponent: <GetRecipientType
                onCardClickCb={(card) => {
                    if (!RequestType) {
                        dispatch(setRecipientType(RecipientType == card.title ? '' : card.title))
                    } else {
                        dispatch(setRecipientType(card.title))
                    }
                }}
                type={RecipientType}
                heading={`How would you describe this recipient? ${!RequestType ? '' : '(optional)'}`}
                description="A recipient is someone who receives income or payments subject to taxation."
                is1042SForm={is1042SForm}
            />,
            backButtonText: "Back",
            prevCb: () => { },
            submitButtonText: "Next",
            submitButtonDisabled: (RequestType || RecipientType) ? false : true,
            submitButtonDisabledInfoCb: () => {
                dispatch(addNotifications({ heading: "Please select the type of recipient", message: "To continue, please select the type of recipient" }))
            },
            submitCb: () => { },
        },
        {
            title: "Basic information",
            stepBodyComponent: <GetBasicInfo />,
            backButtonText: "Back",
            prevCb: () => { },
            submitButtonText: "Next",
            submitButtonDisabled: GeneralValidator.validateRequiredFields(recipientBasicInfo) ? true : false,
            submitButtonDisabledInfoCb: () => {
                dispatch(setRecipientBasicInformationFieldsTouched(true))
                dispatch(addNotifications({ heading: "Error", message: "Please enter valid data" }))
            },
            submitCb: () => { return FEATURE_FLAG.VALIDATE_PAYER_RECIPIENT ? generalValidator.validateAllFieldsRules({...recipientBasicInfo, RecipientTIN : { ...recipientBasicInfo.RecipientTIN, value: tinValue, rules: tinRules}}, (basicInfo) => dispatch(setRecipientBasicInformation(basicInfo))) : true },
        },
        {
            title: "Address information",
            stepBodyComponent: <GetAddressInfo />,
            backButtonText: "Back",
            prevCb: () => { },
            submitButtonText: "Next",
            submitButtonDisabled: GeneralValidator.validateRequiredFields(recipientAddressInformation) || !zipCodeValidationSuccess ? true : false,
            submitButtonDisabledInfoCb: () => {
                dispatch(setRecipientAddressInformationFieldsTouched(true));

                if (GeneralValidator.validateRequiredFields(recipientAddressInformation)) {
                    dispatch(addNotifications({ heading: "Please enter valid data", message: "To continue, please enter the valid address data" }))
                } else if (!zipCodeValidationSuccess) {
                    dispatch(validateZipCode(recipientAddressInformation));
                }
            },
            submitCb: () => { },
        },
        {
            title: "Optional information",
            stepBodyComponent: <GetOptionalInfo />,
            backButtonText: "Back",
            prevCb: () => { },
            submitButtonText: "Next",
            submitButtonDisabled: GeneralValidator.validateRequiredFields(recipientOptionalInformation) ? true : false,
            submitButtonDisabledInfoCb: () => {
                dispatch(setRecipientOptionalInformationFieldsTouched(true))
                dispatch(addNotifications({ heading: "Please enter valid data", message: "To continue, please enter the valid data" }))
            },
            submitCb: () => { fromNecGrid ? submitFormCb({ ...editRecipientData, is1042SForm }) : dispatch(editRecipient({ ...editRecipientData, is1042SForm })) },
        }
    ];

    const EditRecipientJSX = (
        <div style={styles.recipientContainer}>
            <Loadable currentState={loaderProps?.currentState ? loaderProps?.currentState : loadingState.state} isNewLoadable={true} loadingStates={loaderProps?.loadingStates ? loaderProps?.loadingStates : [ADD_RECIPIENT_LOADING_STATES.EDIT_RECIPIENT_LOADING]} messageMap={loaderProps?.messageMap ? loaderProps?.messageMap : ADD_RECIPIENT_MESSAGE_MAP}>
                {recipientEditedSuccessful ? 
                        <AddRecipientSuccess setRefreshStep={setRefreshStep} isEdited={true} /> 
                    : switchToAllStepsSingleView ? <AllStepsSingleView steps={EDIT_RECIPIENT_STEPS} pageTitle={'Edit Recipient'} onSwitchToOtherViewCb={() => setSwitchToAllStepsSingleView(!switchToAllStepsSingleView)} otherViewSwitchIsOpen={switchToAllStepsSingleView} otherViewSwitchLabel='Switch To Other View' /> : <StepperNavigation steps={EDIT_RECIPIENT_STEPS} refreshStep={refreshStep} setRefreshStep={(idx) => setRefreshStep(idx)} customStepperBodyContainerStyles={{ height: "auto" }}
                    customFooterBodyContainer={{ marginTop: "10px" }} showSwitchForOtherView={true} onSwitchToOtherViewCb={() => setSwitchToAllStepsSingleView(!switchToAllStepsSingleView)} otherViewSwitchIsOpen={switchToAllStepsSingleView} otherViewSwitchLabel='Switch To Single View' applyMaxHeightToBodyContainer={"55vh"} />}

                <W8W9PaymentModals
                    submittedRecipient={submittedRecipient}
                    requestType={RequestType}
                    recipientIds={submittedRecipient?.RecipentDetailId}
                    payerId={submittedRecipient?.PayerId}
                />
            </Loadable>
        </div>
    )

    return (
        <>
            {isPopup ?

                <Popup title={""} onCloseCb={onPopupCloseCb} isPopupOpen={isPopupOpen} customPopupSyles={styles.popupStyles} overlayStyles={styles.popupoOverlay} childrenStyles={styles.popupChildrenStyles}>
                    {EditRecipientJSX}
                </Popup>

                :

                EditRecipientJSX
            }
        </>
    )
}